import { logout, TypeBoundAxiosInstance } from '@m43/core';
import { useToast } from '@cad-arc';
import { AxiosError } from 'axios-cad-nx';
import isElectron from 'is-electron';
import isHotKey from 'is-hotkey';
import { ComparatorReturns, ListStore } from '@list-store';
import { compact, each, isString, sortBy, uniq } from 'lodash';
import { logWarn } from '../monitoring/log';
import {
    ApiResult,
    CliAgencyEvent,
    CliArgumentEnumType,
    CliAudit,
    CliAutocompleteMessageRecipient,
    CliAutocompleteOption,
    CliExecutionView,
    CliResource,
    ExecutecliResource,
    FieldNameEnum,
} from '@mark43/cad-api';
import { action, computed, makeObservable, observable } from 'mobx';
import { BaseEditor, Descendant, Editor, NodeEntry, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { QueryableEntityType } from 'mark43-federated-search';
import { MessageTypesForChannel } from '../common/broadcast-channel/types';
import { getDataExchangeFormPanelMatcher } from '../common/helpers/get-data-exchange-form-panel-matcher';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { LocationStore } from '../location-select/location-store';
import { LocationStoreView } from '../location-select/types/locations';
import { SegmentTypeEnumType, segmentTypes } from './constants';
import {
    autoCloseTokenInString,
    CommandLineConfigView,
    convertCliAuditToCommandLineHistoryView,
    createEmptyCommand,
    createFreeTextLocationStoreView,
    DEFAULT_COMMAND_ERROR_MESSAGE,
    escapeRegex,
    findAllSegmentsInString,
    findSegmentForOffset,
    findWordAtOffset,
    getKeyValueFromFormFieldSegment,
    getPotentialUnitCallSigns,
    getRepeatableCliArguments,
    getUnitStaffCounts,
    HelperName,
    helpersEnum,
    HelpObject,
    isFormField,
    isType,
    locationStoreViewToCliAutocompleteOption,
    serialize,
} from './helpers';
import {
    CommandLineClientActionUnionType,
    CommandLineHistoryView,
    CommandSegment,
    CommentSegment,
    DataExchangeInquiryParametersSegment,
    EventSegment,
    isAgencyAssistanceCliClientAction,
    isEditEventCliClientAction,
    isEditUnitCliClientAction,
    isGenerateRenCliClientAction,
    isLogoutCliClientAction,
    isOpenAlertBannerCliClientAction,
    isOpenBoloFormCliClientAction,
    isOpenDexPersonFormCliClientAction,
    isOpenDexResultsCliClientAction,
    isOpenDexVehicleFormCliClientAction,
    isOpenEditHospitalFormCliClientAction,
    isOpenRotationForEventCliClientAction,
    isResyncCliClientAction,
    isSelfInitiateCliClientAction,
    isShowOnMapCliClientAction,
    isUpdateEventLocationCliClientAction,
    isViewEventCliClientAction,
    isViewEventHistoryCliClientAction,
    isViewUnitHistoryCliClientAction,
    Segment,
} from './types';
import { useOpenUnitHistoryPanel } from '../unit-history/hooks/use-open-unit-history-panel';
import { useOpenWorkspacePanel } from '../panels/use-open-workspace-panel';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { useViewLocationOnMap } from '../location/hooks/use-view-location-on-map';
import { CadNextAction } from '../action-broadcaster/types';
import { v4 } from 'uuid';
import type { IntialAssistanceAgencyType } from '../request-assistance/types';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { mapConsolidatedLocationViewToLocationStoreView } from '../location-select/helpers/data-mapping';
import { EsriMapEntityTypeEnum } from '../esri/types';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { useUserActionTracker } from '../monitoring/user-action-tracker/use-user-action-tracker';
import { useDexNextForms } from '../panels/hooks/use-dex-next-forms';

// Slate requires delays (setTimeout) for some actions, e.g. focus, move cursor to end.
// 20ms is good enough in most cases, however, there are some special cases, e.g. select location by clicking the option on web requires longer delay, otherwise, it may not focus or will not move cursor to the end in the cli text box.
// 50ms is long enough for select location by mouse clicking on web as of 02/2025
const SLATE_DEFAULT_DELAY = 20;
// @ts-expect-error TODO: 50ms works fine on demo web production. expose window?.slateLongerDelay for testing in production, if other live tenants still have the issue, we need to find a better number. Remove 'window.slateLongerDelay' once RND-24064 is confirmed to be fixed.
const SLATE_LONGER_DELAY = window?.slateLongerDelay ?? 50;

interface EnabledFeatureConfig {
    allowMultiStashCommandsInCli?: boolean;
    acceptTemporaryUnitOnSelfInitiateCommand?: boolean;
    unitHistory: boolean;
    cadNextDisableLegacyCadAccess: boolean;
    preferNewEventsWindow: boolean;
    disableLegacyMapAndSearches: boolean;
    mapWindowEnabled: boolean;
    cliSelfInitiateCadNext: boolean;
    subpremiseSupport: boolean;
    dexNextEnabled: boolean;
}

interface HelperState {
    activeHelper: HelperName;
    selectedIndex?: number;
    options?: CliAutocompleteOption[];
}

export type CommandEntityMap = Map<
    CliArgumentEnumType | 'LOCATION_LOOKUP',
    Map<string, CliAutocompleteOption | LocationStoreView>
>;

type EnabledFeaturesConfigGetter = () => EnabledFeatureConfig;
type ActionTriggerCallback = (action: CadNextAction[]) => void;
type OpenEventPanelGetter = () => ReturnType<typeof useOpenEventPanel>;
type ViewLocationOnMapGetter = () => ReturnType<typeof useViewLocationOnMap>;
type UserActionTrackerInstanceGetter = () => ReturnType<typeof useUserActionTracker>;
type DexNextFormsGetter = () => ReturnType<typeof useDexNextForms>;
type FetchDexMetadataGetter = () => Promise<{ deviceName: string }>;

export class CommandLineEditorStore {
    public readonly config: CommandLineConfigView;
    public readonly editor: BaseEditor & ReactEditor;
    public readonly locationStore: LocationStore;
    public readonly apiClient: TypeBoundAxiosInstance;
    public deviceName?: string;
    public dexStateUserId?: string | number;
    public history: ListStore<CommandLineHistoryView>;
    public toast: ReturnType<typeof useToast>;
    public locationApi: ReturnType<typeof useLocationApi>;
    public drawerState: ReturnType<typeof useDrawerControls>;
    public openUnitHistoryPanel: ReturnType<typeof useOpenUnitHistoryPanel>;
    public openWorkspacePanel: ReturnType<typeof useOpenWorkspacePanel>;
    public getOpenEventPanel: OpenEventPanelGetter;
    public getViewLocationOnMap: ViewLocationOnMapGetter;
    private getEnabledFeaturesConfig: EnabledFeaturesConfigGetter;
    private triggerActions: ActionTriggerCallback;
    private fieldDisplayNameByFieldName: Record<string, string>;
    private skipKeyUpHandler?: boolean;
    private getUserActionTrackerInstance: UserActionTrackerInstanceGetter;
    private getDexNextForms: DexNextFormsGetter;
    private getFetchDexMetadata: FetchDexMetadataGetter;

    public readonly atomicSegmentTypes: SegmentTypeEnumType[];

    public readonly autoClosingTags: string[];

    public legacyStoreBroadcastChannel: BroadcastChannel;

    @observable public readonly parser: (str?: string) => Segment[];
    @observable public currentSegments: Segment[];

    @observable public helperState: HelperState | undefined;

    @observable public currentCommandId?: number;

    @observable public commandEntityMap: Map<number, CommandEntityMap>;
    @observable public savedLocations: Map<string, LocationStoreView>;
    @observable public validationError?: string;
    @observable public debugOpen: boolean = false;
    @observable public historyCursor: number | undefined;
    @observable public executingCommand: boolean = false;
    @observable public isAutocompleteLoading: boolean = false;

    constructor(
        editor: BaseEditor & ReactEditor,
        config: CommandLineConfigView,
        apiClient: TypeBoundAxiosInstance,
        extras: {
            dexStateUserId?: string;
            toast: ReturnType<typeof useToast>;
            locationApi: ReturnType<typeof useLocationApi>;
            drawerState: ReturnType<typeof useDrawerControls>;
            openUnitHistoryPanel: ReturnType<typeof useOpenUnitHistoryPanel>;
            openWorkspacePanel: ReturnType<typeof useOpenWorkspacePanel>;
            getOpenEventPanel: OpenEventPanelGetter;
            getViewLocationOnMap: ViewLocationOnMapGetter;
            getEnabledFeaturesConfig: EnabledFeaturesConfigGetter;
            triggerActions: ActionTriggerCallback;
            fieldDisplayNameByFieldName: Record<string, string>;
            getUserActionTrackerInstance: UserActionTrackerInstanceGetter;
            getDexNextForms: DexNextFormsGetter;
            getFetchDexMetadata: FetchDexMetadataGetter;
        }
    ) {
        this.config = config;
        this.editor = editor;
        this.locationStore = new LocationStore(null);
        this.savedLocations = new Map();
        this.apiClient = apiClient;
        this.toast = extras.toast;
        this.locationApi = extras.locationApi;
        this.drawerState = extras.drawerState;
        this.openUnitHistoryPanel = extras.openUnitHistoryPanel;
        this.openWorkspacePanel = extras.openWorkspacePanel;
        this.getOpenEventPanel = extras.getOpenEventPanel;
        this.getViewLocationOnMap = extras.getViewLocationOnMap;
        this.getEnabledFeaturesConfig = extras.getEnabledFeaturesConfig;
        this.triggerActions = extras.triggerActions;
        this.fieldDisplayNameByFieldName = extras.fieldDisplayNameByFieldName;
        this.getUserActionTrackerInstance = extras.getUserActionTrackerInstance;
        this.getDexNextForms = extras.getDexNextForms;
        this.getFetchDexMetadata = extras.getFetchDexMetadata;
        this.atomicSegmentTypes = [
            segmentTypes.LOCATION,
            segmentTypes.DISPATCH_AREA,
            segmentTypes.DISPATCH_AREA_GROUP,
            segmentTypes.AGENCY_EVENT_RADIO_CHANNELS,
            segmentTypes.MEMBER,
            segmentTypes.MESSAGE_GROUP,
            segmentTypes.CALL_TAKER_STATION,
            segmentTypes.VEHICLE,
            segmentTypes.AGENCY,
            segmentTypes.LICENSE_PLATE,
            segmentTypes.MESSAGE_RECIPIENT,
        ];

        this.dexStateUserId = extras.dexStateUserId;

        this.history = new ListStore<CommandLineHistoryView>({
            sortingDirective: {
                getSortValue(item) {
                    return item.startTime;
                },
                comparator: (left: string, right: string) =>
                    left.localeCompare(right) as ComparatorReturns,
            },
        });
        this.commandEntityMap = new Map();

        this.createEmptyCommandAndReset();

        const enabledFeatures = this.getEnabledFeaturesConfig();

        this.parser = findAllSegmentsInString({
            commands: config.commands,
            locationToken: config.tokens.locationToken,
            acceptTemporaryUnitOnSelfInitiateCommand:
                enabledFeatures.acceptTemporaryUnitOnSelfInitiateCommand,
            cliArgumentForArgumentDefinition: config.cliArgumentForArgumentDefinition,
            dexInquiryParamsToken: config.tokens.dexInquiryParamsToken,
            eventToken: config.tokens.eventToken,
            formFieldDelimiter: config.tokens.formFieldDelimiter,
            formFieldSeparator: config.tokens.formFieldSeparator,
            highPriorityToken: config.tokens.highPriorityToken,
            licensePlateToken: config.tokens.licensePlateToken,
        });

        this.autoClosingTags = compact([
            config.tokens.locationToken,
            config.tokens.licensePlateToken,
        ]);

        this.currentSegments = [];

        this.legacyStoreBroadcastChannel = new BroadcastChannel('legacy-store-broadcast');

        this.getDexMetadata();
        makeObservable(this);
    }

    @action('getDexMetadata')
    getDexMetadata = async (): Promise<void> => {
        const { deviceName } = await this.getFetchDexMetadata();
        this.deviceName = deviceName;
    };

    @action('createEmptyCommandAndReset')
    createEmptyCommandAndReset = (commandText?: string): void => {
        const initialCommand = createEmptyCommand(commandText);

        this.history.upsertItems([initialCommand]);
        this.setCurrentCommandId(initialCommand.id);
        this.resetEditorText(initialCommand.commandText);
        this.commandEntityMap.set(initialCommand.id, new Map());
    };

    @action('fetchHistory')
    fetchHistory = async (): Promise<void> => {
        try {
            const resp = await this.apiClient.request<CliResource.GetCliHistory>({
                method: 'POST',
                url: 'cli/history',
                data: { version: 2 },
            });

            if (resp?.data.data?.cliAudits) {
                this.upsertHistory(resp.data.data.cliAudits);
            }
        } catch (e) {
            // Error fetching history - silently handled
        }
    };

    @action('toggleDebugOpen')
    toggleDebugOpen = (): void => {
        if (process.env.NODE_ENV === 'development') {
            this.debugOpen = !this.debugOpen;
        }
    };

    @action('upsertHistory')
    upsertHistory = (cliAudits: CliAudit[]): void => {
        const commandLineHistoryViews = cliAudits.map(convertCliAuditToCommandLineHistoryView);

        this.history.upsertItems(commandLineHistoryViews);

        commandLineHistoryViews.forEach((historyView) => {
            try {
                if (historyView.commandEntityMap) {
                    const hydratedMap: CommandEntityMap = new Map();
                    Object.entries(historyView.commandEntityMap).forEach((entry) => {
                        hydratedMap.set(entry[0] as any, new Map(entry[1]));

                        // Locations live in a separate part of state for a few complex reasons.
                        // So, when we detect a location object we need to save it in savedLocations
                        // as well as the Hydrated Map. The Hydrated Map keeps a record of the actual segment
                        // text, while the savedLocations map is a reference to the underlying location object.
                        if (entry[0] === 'LOCATION_LOOKUP') {
                            entry[1].forEach(
                                (locationTuple: [string, LocationStoreView] | undefined) => {
                                    if (locationTuple) {
                                        const [segmentText, location] = locationTuple;

                                        this.saveLocation(segmentText, location);
                                    }
                                }
                            );
                        }
                    });

                    this.commandEntityMap.set(historyView.id, hydratedMap);
                } else {
                    this.commandEntityMap.set(historyView.id, new Map());
                }
            } catch (e) {
                // Error hydrating entity map - silently handled
            }
        });
    };

    @action('setHelperSelectedIndex')
    setHelperSelectedIndex = (index?: number): void => {
        if (this.helperState) {
            this.helperState.selectedIndex = index;
        }
    };

    @action('setHelperOptions')
    setHelperOptions = (options?: CliAutocompleteOption[]): void => {
        if (this.helperState) {
            this.helperState.options = options;
            if (options) {
                this.setHelperSelectedIndex(options.length - 1);
            }
        }
    };

    @action('helperHandleKeyDown')
    helperHandleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        const currentIndex = this.helperState?.selectedIndex;

        const options =
            this.helperState?.activeHelper === 'LOCATION'
                ? this.locationStore.renderedLocationData.renderedList
                : this.helperState?.options;
        const totalCount = options?.length;

        if (totalCount) {
            if (isHotKey('Up', event)) {
                if (typeof currentIndex === 'undefined') {
                    this.setHelperSelectedIndex(totalCount - 1);
                } else {
                    this.setHelperSelectedIndex(Math.max(0, currentIndex - 1));
                }
            } else if (isHotKey('Down', event)) {
                if (typeof currentIndex !== 'undefined') {
                    this.setHelperSelectedIndex(Math.min(totalCount - 1, currentIndex + 1));
                }
            } else if (isHotKey('Enter', event)) {
                if (options && typeof currentIndex !== 'undefined') {
                    let selectedOption: CliAutocompleteOption | undefined;

                    if (this.helperState?.activeHelper === 'LOCATION') {
                        const location =
                            this.locationStore.renderedLocationData.renderedList[currentIndex];
                        if (location) {
                            this.selectLocation(location);
                        }
                    } else {
                        selectedOption = (options as CliAutocompleteOption[])[currentIndex];
                    }

                    if (selectedOption) {
                        this.selectOption(selectedOption);
                    }
                }
            }
        }
    };

    @action('focus')
    focus = (): void => {
        ReactEditor.focus(this.editor);
    };

    // Slate quirk requires doing this in a short timeout so the editor state changes can flush
    // before moving the cursor.
    @action('moveToEnd')
    moveToEnd = (delay = SLATE_DEFAULT_DELAY): void => {
        setTimeout(() => {
            Transforms.move(this.editor, { distance: 1, unit: 'line', reverse: false });
        }, delay);
    };

    // normally editorStore.focus() then editorStore.moveToEnd() will work to focus the cli input box and move the cursor to the end.
    // always try normal way first, unless there are focus/cursor issue
    @action('delayedFocusAndMoveToEnd')
    delayedFocusAndMoveToEnd = (delay = SLATE_DEFAULT_DELAY): void => {
        setTimeout(() => {
            this.focus();
            this.moveToEnd(delay);
        }, delay);
    };

    @action('externalSelectOption')
    externalSelectOption = (option: CliAutocompleteOption): void => {
        this.selectOption(option);
        ReactEditor.focus(this.editor);
        this.setActiveHelper();
    };

    @action('selectOption')
    selectOption = (option: CliAutocompleteOption): void => {
        const currentCommandId = this.currentCommandId;
        const currentCommand = this.currentCommand;
        const currentSegment = this.getCurrentSegment;
        const oldText = this.currentText;

        if (currentCommandId && currentCommand && currentSegment && option) {
            const beforeText = oldText.slice(0, currentSegment.start);
            const afterText = oldText.slice(currentSegment.start + currentSegment.length);
            const newText = `${beforeText}${option.segmentText} ${afterText}`;
            currentCommand.commandText = newText;
            this.history.upsertItems([currentCommand]);
            this.resetEditorText(newText);
            this.saveEntityForCommand(currentCommandId, option);
        }
    };

    @action('selectLocation')
    selectLocation = async (location: LocationStoreView, regainFocus?: boolean): Promise<void> => {
        const selectedOption = locationStoreViewToCliAutocompleteOption(
            this.config.tokens.locationToken,
            location,
            this.getEnabledFeaturesConfig().subpremiseSupport
        );

        if (selectedOption) {
            if (location.areCoordinatesVerified === false) {
                // Forced location, continue without classifying.

                this.saveLocation(selectedOption.segmentText, location);
                this.selectOption(selectedOption);
            } else if (location.streetAddress) {
                try {
                    const resp = await this.locationApi.resolveEntityLinksForLocation(location);

                    this.saveLocation(
                        selectedOption.segmentText,
                        mapConsolidatedLocationViewToLocationStoreView(resp)
                    );
                    this.selectOption(selectedOption);
                    this.setActiveHelper();
                } catch (e) {
                    this.saveLocation(selectedOption.segmentText, location);
                    this.selectOption(selectedOption);
                }
            }

            if (regainFocus) {
                this.setActiveHelper();
                // it takes longer time to focus and move cursor to end on web when select location by mouse clicking.
                // increase delay for focus and move to end
                this.delayedFocusAndMoveToEnd(SLATE_LONGER_DELAY);
            }
        }
    };

    @action('saveLocation')
    saveLocation = (segmentText: string, location: LocationStoreView): void => {
        this.savedLocations.set(segmentText.toUpperCase(), location);
    };

    @action('saveEntityForCommand')
    saveEntityForCommand = (commandId: number, option: CliAutocompleteOption): void => {
        if (!this.commandEntityMap.has(commandId)) {
            this.commandEntityMap.set(commandId, new Map());
        }

        if (!this.commandEntityMap.get(commandId)?.has(option.cliArgument)) {
            this.commandEntityMap.get(commandId)?.set(option.cliArgument, new Map());
        }

        this.commandEntityMap
            .get(commandId)
            ?.get(option.cliArgument)
            ?.set(option.segmentText.toUpperCase(), option);
    };

    @action('setActiveHelper')
    setActiveHelper = (helper?: HelperName): void => {
        if (helper) {
            if (helper !== this.helperState?.activeHelper) {
                this.helperState = {
                    activeHelper: helper,
                };
            }
        } else {
            if (this.helperState?.activeHelper && this.helperState.activeHelper === 'LOCATION') {
                this.locationStore.setItems([]);
            }
            this.helperState = undefined;
            this.setHelperOptions();
        }
    };

    @action('onChange')
    onChange = (value: Descendant[]): void => {
        const string = serialize(value);
        const segments = this.parser(string);

        this.currentSegments = segments;

        if (this.currentCommand?.status === 'ACTIVE') {
            this.history.upsertItems([{ ...this.currentCommand, commandText: string }]);
        }
    };

    @action('onKeyDown')
    onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        const editor = this.editor;
        const currentOffset = editor.selection?.anchor.offset;
        const currentText = this.currentText;
        const currentSegment = this.getCurrentSegment;
        const activeCommandSegment = this.activeCommandSegment;

        if (process.env.NODE_ENV === 'development' && isHotKey('Ctrl+Shift+D', event)) {
            this.toggleDebugOpen();
        }

        if (this.helperState?.activeHelper) {
            if (
                isHotKey('Up', event) ||
                isHotKey('Down', event) ||
                (isHotKey('Enter', event) && this.helperState.selectedIndex !== -1) ||
                isHotKey('Escape', event)
            ) {
                this.stopEvent(event);
                this.helperHandleKeyDown(event);
                return;
            }
        }

        if (isHotKey('Mod+Enter', event)) {
            this.moveCurrentToStash();
            this.getUserActionTrackerInstance()?.trackStashCommand({
                actionSource: 'Keyboard',
                stashTrigger: 'SHORTCUT',
                stashedCount: this.stashedItems.length,
                lastStashed: this.stashedItems?.[this.stashedItems?.length - 1]?.commandText,
            });
        }

        if (isHotKey('Mod+Up', event) || isHotKey('Mod+Down', event)) {
            this.stopEvent(event);
            if (this.getEnabledFeaturesConfig().allowMultiStashCommandsInCli) {
                if (isHotKey('Mod+Up', event)) {
                    this.moveCurrentToStash();
                } else if (isHotKey('Mod+Down', event)) {
                    this.moveCurrentToStash(true);
                    this.moveToBottomStashedCommand();
                }
            } else {
                this.toggleStashedCommand();

                return false;
            }
        }

        if (isHotKey('Up', event)) {
            this.stopEvent(event);
            this.goUpInHistory();
        }

        if (isHotKey('Down', event)) {
            this.stopEvent(event);
            this.goDownInHistory();
        }

        if (isHotKey('Tab', event)) {
            if (currentSegment && this.atomicSegmentTypes.includes(currentSegment?.segmentType)) {
                this.stopEvent(event);
                this.tabOutOfCurrentSegment();
                return;
            }
        }

        if (isHotKey('Enter', event)) {
            this.stopEvent(event);
            this.safeExecuteCommand();
        }

        each(this.autoClosingTags, (autoClosingTag) => {
            if (event.key === autoClosingTag && !currentSegment) {
                this.handleAutoCloseTags(event, autoClosingTag);
                return false;
            }
        });

        if (
            this.config.tokens.formFieldSeparator &&
            activeCommandSegment &&
            !currentSegment &&
            event.key === this.config.tokens.formFieldSeparator
        ) {
            const { word } = findWordAtOffset(currentText, currentOffset!);
            const key = (word || '').toUpperCase().trim();

            const segmentType = this.config.cliArgumentForArgumentDefinition[key];
            const currentCommand = this.currentCommand;

            if (
                currentCommand &&
                (segmentType === 'MEMBER' ||
                    segmentType === 'MESSAGE_GROUP' ||
                    segmentType === 'CALL_TAKER_STATION' ||
                    segmentType === 'MESSAGE_RECIPIENT')
            ) {
                const { offset: _newOffset, string: newText } = autoCloseTokenInString({
                    token: this.config.tokens.formFieldSeparator,
                    lastKeyPressed: event.key,
                    offset: currentOffset!,
                    string: currentText,
                    withinString: true,
                    closingToken: this.config.tokens.formFieldDelimiter,
                });

                this.stopEvent(event);

                currentCommand.commandText = newText || '';
                this.history.upsertItems([currentCommand]);
                this.resetEditorText(newText || '');
                Transforms.move(editor, {
                    distance: 1,
                    unit: 'character',
                    reverse: true,
                });
            }
        }
    };

    @action('setValidationError')
    setValidationError = (error?: string): void => {
        this.validationError = error;
    };

    @action('validateCommand')
    validateCommand = async (executionView: CliExecutionView): Promise<string | undefined> => {
        try {
            await this.apiClient.request<ExecutecliResource.ValidateCommandLine>({
                url: 'execute/cli/validate',
                method: 'POST',
                params: {
                    cli_version: 2,
                },
                data: executionView,
                shouldFailSilently: true,
            });
        } catch (e) {
            const errorMessage = (e as AxiosError<ApiResult>).response?.data.error;
            this.getUserActionTrackerInstance()?.trackCommandLine({
                commandSegment: this.activeCommandSegment,
                command: this.currentCommand,
                errorMessage: errorMessage ?? '',
                validator: 'FE',
            });
            return errorMessage;
        }
    };

    @action('postLegacyMessage')
    postLegacyMessage = (message: MessageTypesForChannel<'legacy-store-broadcast'>): void => {
        this.legacyStoreBroadcastChannel.postMessage(message);
    };

    @action('setExecutingCommand')
    setExecutingCommand = (executing: boolean) => {
        this.executingCommand = executing;
    };

    @action('safeExecuteCommand')
    safeExecuteCommand = async (): Promise<void> => {
        if (!this.executingCommand) {
            this.setExecutingCommand(true);
            try {
                await this.executeCommand();
            } catch (e) {
                // Error executing command - silently handled
            } finally {
                this.setExecutingCommand(false);
            }
        }
    };

    @action('executeCommand')
    executeCommand = async (): Promise<void> => {
        const currentCommandText = this.currentText.toUpperCase();
        const activeCommandSegment = this.activeCommandSegment;
        const currentCommandId = this.currentCommandId;
        const currentCommand = this.currentCommand;
        const executionView = this.executionView;
        const currentSegments = this.currentSegments;

        if (activeCommandSegment?.segmentData?.cliArgument === 'HELP') {
            this.setActiveHelper('HELP');
            return;
        }

        if (!activeCommandSegment) {
            this.skipKeyUpHandler = true;
            this.setValidationError('Invalid Parameters');
            this.getUserActionTrackerInstance()?.trackCommandLine({
                command: currentCommand,
                executionView,
                errorMessage: 'Invalid Parameters',
                validator: 'FE',
            });

            return;
        }

        if (currentCommandId && currentCommand) {
            if (executionView) {
                this.setValidationError();

                const validationErrors = await this.validateCommand(executionView);

                if (validationErrors) {
                    this.setValidationError(validationErrors);
                } else {
                    // Begin History Mutation Logic
                    // In order to appear as responsive as possible, update the history and reset the CLI input right away and let the actual
                    // execution happen asynchronously. Any inline-validation errors that the user would actually need to address are already
                    // accounted for by this point (because the validation already ran above).
                    // Repeatable Command Logic
                    if (activeCommandSegment?.segmentData.shouldRepeat) {
                        // First, grab the types of segments that should be repeated in the new command.
                        const repeatableCliArguments = getRepeatableCliArguments(
                            activeCommandSegment.segmentData
                        );

                        // Then, grab the user input for those segments
                        const repeatedSegments = compact(
                            currentSegments.filter((segment) => {
                                return repeatableCliArguments.includes(
                                    segment.segmentType as CliArgumentEnumType
                                );
                            })
                        ).map((segment) => segment.segmentData);

                        // Then, construct a new command starting with the argument definition, then the repeated segments and finally the starting token (via config.)
                        const startingToken = Object.keys(
                            this.config.cliArgumentForArgumentDefinition
                        ).find(
                            (key) =>
                                this.config.cliArgumentForArgumentDefinition[key] ===
                                activeCommandSegment.segmentData.startingArgumentForRepeat
                        );

                        const combined = compact([
                            activeCommandSegment.segmentData.argumentDefinition,
                            ...repeatedSegments,
                            startingToken,
                        ]);

                        this.createEmptyCommandAndReset(combined.join(' '));
                    } else {
                        this.createEmptyCommandAndReset();
                    }

                    this.historyCursor = undefined;

                    this.history.upsertItems([
                        {
                            id: currentCommandId,

                            commandText: currentCommandText,
                            cliArgumentConfigurationId: currentCommand.cliArgumentConfigurationId,
                            status: 'RUNNING',
                            startTime: new Date().toISOString(),
                            endTime: new Date().toISOString(),
                        },
                    ]);

                    // End History Mutation Logic

                    try {
                        const resp = await this.apiClient.request<ExecutecliResource.ExecuteCommandLineV2>(
                            {
                                url: 'execute/cli/v2',
                                method: 'POST',
                                params: { cli_version: 2 },
                                data: executionView,
                            }
                        );

                        if (resp.data.data?.cliAudit) {
                            this.history.removeItems([currentCommandId]);
                            this.upsertHistory([resp.data.data.cliAudit]);
                        }

                        let rawEventNumber = this.currentSegments.find(
                            (seg) => seg.segmentType === 'EVENT'
                        )?.segmentData as string;

                        if (rawEventNumber && this.config.tokens.eventToken) {
                            rawEventNumber = rawEventNumber.replace(
                                this.config.tokens.eventToken,
                                ''
                            );
                        }

                        const cliClientActions = resp.data.data?.cliClientActions;

                        if (cliClientActions) {
                            for (const cliClientAction of cliClientActions) {
                                this.handleCliClientAction(cliClientAction);
                            }
                        }
                    } catch (e) {
                        const errorMessage = (e as AxiosError<ApiResult>).response?.data.error;
                        const statusCode = (e as AxiosError<ApiResult>).response?.status;

                        this.history.upsertItems([
                            {
                                ...currentCommand,
                                status: 'ERROR',
                                errors:
                                    errorMessage && statusCode !== 500
                                        ? errorMessage
                                        : DEFAULT_COMMAND_ERROR_MESSAGE,
                            },
                        ]);
                        if (!errorMessage || statusCode === 500) {
                            this.toast({
                                status: 'default',
                                description: DEFAULT_COMMAND_ERROR_MESSAGE,
                            });
                        }

                        this.getUserActionTrackerInstance()?.trackCommandLine({
                            command: currentCommand,
                            commandSegment: activeCommandSegment,
                            errorMessage: errorMessage ?? '',
                            statusCode: statusCode ?? '',
                            validator: 'BE',
                        });

                        this.createEmptyCommandAndReset();
                    }
                }
            }
        }
        this.getUserActionTrackerInstance()?.trackCommandLine({
            command: currentCommand,
            commandSegment: activeCommandSegment,
            executionView,
        });
    };

    @action('onKeyUp')
    onKeyUp: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (this.skipKeyUpHandler) {
            this.skipKeyUpHandler = false;
            return;
        }

        const editor = this.editor;
        const currentCommandId = this.currentCommandId;
        const currentOffset = editor.selection?.anchor.offset;
        const currentText = this.currentText;
        const currentSegment = this.getCurrentSegment;

        const isInHelper = helpersEnum[currentSegment?.segmentType as HelperName];
        const input = event.currentTarget.firstElementChild as HTMLDivElement;
        const isInHistoryCommand =
            this.historyCursor && this.historyCursor > -1 && this.visibleItems[this.historyCursor];

        if (input && currentOffset === input?.innerText.length) {
            input.scrollLeft = input.scrollWidth;
        }

        // There is a current command, but we've emptied out the text so we need to reset
        // the command entities.
        if (currentCommandId && currentText?.trim().length === 0) {
            this.commandEntityMap.set(currentCommandId, new Map());
        }

        if (this.helperState && !isInHelper) {
            this.setActiveHelper(undefined);
        }

        if (isInHelper) {
            const hasChangedHistoryCommand =
                isInHistoryCommand && isInHistoryCommand.commandText !== currentText;
            if (!isInHistoryCommand || hasChangedHistoryCommand) {
                this.setActiveHelper(currentSegment?.segmentType as HelperName);
            }
        }

        this.setValidationError();
    };

    @action('toggleStashedCommand')
    toggleStashedCommand = (): void => {
        if (this.getEnabledFeaturesConfig().allowMultiStashCommandsInCli) {
            return;
        } else {
            if (this.stashedItems.length) {
                this.replaceCurrentWithStashedCommand();
            } else {
                this.moveCurrentToStash();
            }
        }
    };

    @action('moveCurrentToStash')
    moveCurrentToStash = (moveToTop?: boolean): void => {
        const topStashedItem = this.stashedItems[0];
        const currentCommand = this.currentCommand;
        if (currentCommand?.commandText.length) {
            currentCommand.status = 'STAGED';

            if (moveToTop && topStashedItem) {
                currentCommand.startTime = (Number(topStashedItem.startTime) - 1).toString();
            }

            const newEmptyCommand = createEmptyCommand();

            this.history.upsertItems([currentCommand, newEmptyCommand]);
            this.setCurrentCommandId(newEmptyCommand.id);
        }
    };

    @action('replaceCurrentWithStashedCommand')
    replaceCurrentWithStashedCommand = (): void => {
        const currentCommand = this.currentCommand;
        const stashedCommand = this.stashedItems[0];

        if (stashedCommand && currentCommand) {
            if (currentCommand.commandText.length) {
                currentCommand.status = 'STAGED';
            }
            stashedCommand.status = 'ACTIVE';

            this.history.upsertItems([stashedCommand, currentCommand]);
            this.setCurrentCommandId(stashedCommand.id);
        }
    };

    @action('moveToBottomStashedCommand')
    moveToBottomStashedCommand = (): void => {
        const bottomStashedCommand = this.stashedItems[this.stashedItems.length - 1];

        if (bottomStashedCommand) {
            bottomStashedCommand.status = 'ACTIVE';
            this.history.upsertItems([bottomStashedCommand]);
            this.setCurrentCommandId(bottomStashedCommand.id);
        }
    };

    @action('removeStashedCommand')
    removeStashedCommand = (id: number): void => {
        this.history.removeItems([id]);
    };

    @action('goUpInHistory')
    goUpInHistory = (): void => {
        let commandIndexToCopy: number | undefined = undefined;

        if (typeof this.historyCursor === 'undefined') {
            // If not set, select last row
            // if selecting first row, skip
            // otherwise select selectedIndex - 1
            if (this.totalCount) {
                commandIndexToCopy = this.visibleItems.length - 1;
            }
        } else {
            if (this.historyCursor > 0) {
                commandIndexToCopy = this.historyCursor - 1;
            }
        }

        if (typeof commandIndexToCopy !== 'undefined') {
            this.historyCursor = commandIndexToCopy;

            const historyCommand = this.visibleItems[this.historyCursor];

            if (historyCommand) {
                this.resetEditorText(historyCommand.commandText);

                const historyCommandEntityMap = this.commandEntityMap.get(historyCommand.id);

                if (this.currentCommandId && historyCommandEntityMap) {
                    this.commandEntityMap.set(this.currentCommandId, historyCommandEntityMap);
                }
            }
        }
    };

    @action('goDownInHistory')
    goDownInHistory = (): void => {
        let commandIndexToCopy: number | undefined = undefined;

        if (typeof this.historyCursor !== 'undefined') {
            if (this.totalCount) {
                if (this.historyCursor < this.totalCount - 1) {
                    commandIndexToCopy = this.historyCursor + 1;
                } else {
                    this.resetEditorText('');
                }
            }
        }

        if (typeof commandIndexToCopy === 'undefined') {
            this.historyCursor = commandIndexToCopy;
        }

        if (typeof commandIndexToCopy !== 'undefined') {
            this.historyCursor = commandIndexToCopy;

            const historyCommand = this.visibleItems[this.historyCursor];

            if (historyCommand) {
                this.resetEditorText(historyCommand.commandText);

                const historyCommandEntityMap = this.commandEntityMap.get(historyCommand.id);
                if (this.currentCommandId && historyCommandEntityMap) {
                    this.commandEntityMap.set(this.currentCommandId, historyCommandEntityMap);
                }
            }
        }
        // if not set, skip
        // if selecting last row, set undefined
        // otherwise select selectedIndex + 1
    };

    @action('setCurrentCommandByIndex')
    setCurrentCommandByIndex = (index?: number): void => {
        if (typeof index !== 'undefined' && this.visibleItems[index]) {
            this.setCurrentCommandId(this.visibleItems[index].id);
        }
    };

    @action('setCurrentCommandId')
    setCurrentCommandId = (id: number): void => {
        if (this.history.hasItem(id)) {
            this.currentCommandId = id;

            const newCommand = this.history.getItemById(id);

            if (newCommand) {
                this.resetEditorText(newCommand.commandText);
            } else {
                logWarn('CLI: Unexpectedly could not find command by id');
            }
        }
    };

    @action('resetEditorText')
    resetEditorText = (commandText: string): void => {
        Transforms.removeNodes(this.editor, { mode: 'highest', hanging: true });
        Transforms.insertNodes(this.editor, {
            type: 'paragraph',
            children: [{ text: commandText || '' }],
        });
    };

    @action('stopEvent')
    stopEvent: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        event.stopPropagation();
        event.preventDefault();
    };

    @action('selectHelpCommand')
    selectHelpCommand = (helpCommand: HelpObject): void => {
        this.setActiveHelper(undefined);
        const text = `${helpCommand.cliArgumentConfigurationView.argumentDefinition} `;
        const currentCommand = this.currentCommand;
        if (currentCommand) {
            currentCommand.commandText = text;

            this.history.upsertItems([currentCommand]);

            this.resetEditorText(text);

            this.delayedFocusAndMoveToEnd();
        }
    };

    @action('escapeHelpWindow')
    escapeHelpWindow = (): void => {
        this.setActiveHelper(undefined);
        this.resetEditorText('');
        this.delayedFocusAndMoveToEnd();
    };

    @action('tabOutOfCurrentSegment')
    tabOutOfCurrentSegment = (regainFocus?: boolean): void => {
        const currentSegment = this.getCurrentSegment;
        const currentText = this.currentText;

        if (currentSegment) {
            if (currentSegment.segmentType === 'LOCATION') {
                let freeText = currentSegment.segmentData;

                if (this.config.tokens.locationToken) {
                    freeText = freeText.replace(
                        new RegExp(escapeRegex(this.config.tokens.locationToken), 'g'),
                        ''
                    );
                }
                this.saveLocation(
                    currentSegment.segmentData.toUpperCase(),
                    createFreeTextLocationStoreView(freeText)
                );
            }

            const newOffset = currentSegment.start + currentSegment.length + 1;
            if (currentText.length < newOffset) {
                Transforms.move(this.editor, { distance: 1, unit: 'line', reverse: false });
                Transforms.insertText(this.editor, ' ');
            }

            Transforms.select(this.editor, {
                anchor: Editor.end(this.editor, []),
                focus: Editor.end(this.editor, []),
            });

            if (regainFocus) {
                this.setActiveHelper();
                // it takes longer time to focus and move cursor to end on web when select location by mouse clicking.
                // increase delay for focus and move to end.
                this.delayedFocusAndMoveToEnd(SLATE_LONGER_DELAY);
            }
        }
    };

    @action('handleAutoCloseTags')
    handleAutoCloseTags = (
        event: React.KeyboardEvent<HTMLDivElement>,
        autoClosingTag: string
    ): void => {
        const editor = this.editor;
        const currentCommand = this.currentCommand;
        const currentText = this.currentText;
        const anchorOffset = editor.selection?.anchor.offset;
        if (currentCommand && typeof anchorOffset !== 'undefined') {
            const { offset: _newOffset, string: newText } = autoCloseTokenInString({
                token: autoClosingTag,
                lastKeyPressed: event.key,
                offset: anchorOffset,
                string: currentText,
            });

            if (newText == null || _newOffset === null) {
                return;
            }

            this.stopEvent(event);

            currentCommand.commandText = newText || '';
            this.history.upsertItems([currentCommand]);
            this.resetEditorText(newText || '');

            Transforms.select(this.editor, { path: [0, 0], offset: _newOffset });
        }
    };

    @computed
    get activeCommandSegment(): CommandSegment | undefined {
        return this.currentSegments.find(isType('COMMAND'));
    }

    @computed
    get getCurrentSegment(): Segment | null | undefined {
        const currentOffset = this.editor.selection?.anchor.offset;

        const segments = this.currentSegments;

        if (currentOffset) {
            return findSegmentForOffset(segments, currentOffset);
        }
    }

    @computed
    get helperSearchText(): string | null | undefined {
        const currentSegment = this.getCurrentSegment;

        if (currentSegment) {
            if (isFormField(currentSegment)) {
                const { value } = getKeyValueFromFormFieldSegment(currentSegment, {
                    formFieldSeparator: this.config.tokens.formFieldSeparator,
                    formFieldDelimiter: this.config.tokens.formFieldDelimiter,
                });

                return value;
            }

            if (currentSegment.segmentType === 'EVENT') {
                return currentSegment.segmentData.replace(
                    new RegExp(escapeRegex(this.config.tokens.eventToken), 'g'),
                    ''
                );
            }

            if (currentSegment.segmentType === 'LOCATION') {
                if (isString(currentSegment.segmentData)) {
                    return currentSegment.segmentData.replace(
                        new RegExp(escapeRegex(this.config.tokens.locationToken), 'g'),
                        ''
                    );
                } else {
                    throw new Error('Found Location View in segment data');
                }
            }
        }

        return;
    }

    get currentText(): string {
        return serialize(this.editor.children);
    }

    @computed
    get items(): CommandLineHistoryView[] {
        return this.history.data.list;
    }

    @computed
    get stashedItems(): CommandLineHistoryView[] {
        return sortBy(
            this.items.filter((item) => item.status === 'STAGED'),
            (i) => i.startTime
        );
    }

    @computed
    get visibleItems(): CommandLineHistoryView[] {
        return this.items.filter((item) => !item.hidden);
    }

    @computed
    get currentCommandIndex(): number {
        return this.items.findIndex((c) => c.id === this.currentCommandId);
    }

    @computed
    get currentCommand(): CommandLineHistoryView | undefined {
        return this.items[this.currentCommandIndex];
    }

    @computed
    get totalCount(): number {
        return this.visibleItems.length;
    }

    @computed
    get executionView(): CliExecutionView | undefined {
        const currentCommandId = this.currentCommandId;
        const activeCommandSegment = this.activeCommandSegment;
        if (currentCommandId && activeCommandSegment) {
            const entityMap = this.commandEntityMap.get(currentCommandId);
            const comments = this.currentSegments
                .filter((s) => s.segmentType === 'COMMENT')
                .map((s) => (s as CommentSegment).segmentData.substring(1)?.toUpperCase());

            const dexInquiryParams = this.currentSegments
                .filter((s) => s.segmentType === 'DATA_EXCHANGE_INQUIRY_PARAMETERS')
                .map((s) =>
                    (s as DataExchangeInquiryParametersSegment).segmentData.split(
                        this.config.tokens.dexInquiryParamsToken || ''
                    )
                );

            const isHighPriority: boolean[] = uniq(
                this.currentSegments
                    .filter((s) => s.segmentType === 'IS_HIGH_PRIORITY')
                    .map(() => true)
            );

            const commandText = this.currentText.toUpperCase();

            const potentialUnitCallSigns = getPotentialUnitCallSigns(
                commandText,
                this.currentSegments
            );

            const consolidatedLocationViews = this.locationExecutionValuesForCommandId();

            return {
                hospitalIds: this.executionValuesForCommandIdAndSegmentType('HOSPITAL', entityMap),
                potentialUnitCallSigns,
                hydrated: false,
                deviceName: this.deviceName,
                dexStateUserId: `${this.dexStateUserId ?? ''}`,
                serializedCommandEntityMap: this.hydrateEntityMapWithLocations(entityMap),
                cliArgumentConfigurationId: activeCommandSegment.segmentData.id,
                commandText,
                dexInquiryParams,
                oriAliasIds: this.executionValuesForCommandIdAndSegmentType('ORI_ALIAS', entityMap),
                isHighPriority,
                locations: [],
                consolidatedLocationViews,
                comments,
                agencyEvents: this.eventExecutionValuesFormCommandIdAndSegmentType(entityMap),
                agencyEventIds: this.executionValuesForCommandIdAndSegmentType('EVENT', entityMap),
                agencyEventRadioChannelIds: this.executionValuesForCommandIdAndSegmentType(
                    'AGENCY_EVENT_RADIO_CHANNELS',
                    entityMap
                ),
                agencyIds: this.executionValuesForCommandIdAndSegmentType('AGENCY', entityMap),
                cadRequestProfileIds: this.executionValuesForCommandIdAndSegmentType(
                    'CAD_REQUEST_PROFILE',
                    entityMap
                ),
                callForServiceIds: this.executionValuesForCommandIdAndSegmentType(
                    'CALL_FOR_SERVICE',
                    entityMap
                ),
                callTakerStationIds: this.executionValuesForCommandIdAndSegmentType(
                    'CALL_TAKER_STATION',
                    entityMap
                ),
                dispatchAreaIds: this.executionValuesForCommandIdAndSegmentType(
                    'DISPATCH_AREA',
                    entityMap
                ),
                dispatchGroupIds: this.executionValuesForCommandIdAndSegmentType(
                    'DISPATCH_AREA_GROUP',
                    entityMap
                ),
                dispositionAttrIds: this.executionValuesForCommandIdAndSegmentType(
                    'DISPOSITION',
                    entityMap
                ),
                messageGroupIds: this.executionValuesForCommandIdAndSegmentType(
                    'MESSAGE_GROUP',
                    entityMap
                ),
                primarySubdivisionIds: this.executionValuesForCommandIdAndSegmentType(
                    'PRIMARY_SUBDIVISION',
                    entityMap
                ),
                rotationServiceTypeIds: this.executionValuesForCommandIdAndSegmentType(
                    'ROTATION_SERVICE_TYPE',
                    entityMap
                ),
                stationIds: this.executionValuesForCommandIdAndSegmentType('STATION', entityMap),
                tagNumberAttrIds: this.executionValuesForCommandIdAndSegmentType(
                    'VEHICLE',
                    entityMap
                ),
                units: [],
                unitTagIds: this.executionValuesForCommandIdAndSegmentType('UNIT_TAG', entityMap),
                unitTimerTimes: this.timeExecutionValuesForCommandId(),
                userIds: this.executionValuesForCommandIdAndSegmentType('MEMBER', entityMap),
                unitStaffCounts: getUnitStaffCounts(
                    this.currentSegments,
                    this.config.cliArgumentForArgumentDefinition
                ),
                messageRecipients: this.executionValuesForMessageRecipient(entityMap),
            };
        }
    }

    hydrateEntityMapWithLocations = (entityMap?: CommandEntityMap) => {
        if (entityMap) {
            // We need to persist a record of the *actual* locations that get used during a command,
            // so that we can rehydrate the cliAudits in the history view. Otherwise, when you select
            // a command that had a location it won't properly detect its original value and won't display
            // verified, unverified status etc. It'd also result in an unknown set of other bugs where there's
            // a location segment in the CLI but no accompanying location object. The behavior of that would change
            // based on the actual command in question.
            entityMap.set('LOCATION_LOOKUP', new Map<string, LocationStoreView>());
            const savedLocations = this.savedLocations;
            const applicableLocationSegments = this.currentSegments?.filter(
                (s) => s.segmentType === 'LOCATION'
            ) as EventSegment[];

            applicableLocationSegments.forEach((segment) => {
                const existingSavedEntity = entityMap.get('LOCATION')?.get(segment.segmentData);
                const locationObject = savedLocations.get(segment.segmentData);

                if (existingSavedEntity && locationObject) {
                    entityMap.get('LOCATION_LOOKUP')?.set(segment.segmentData, locationObject);
                }
            });
        }

        return entityMap ? JSON.stringify(Object.fromEntries(entityMap)) : '';
    };

    timeExecutionValuesForCommandId = (): number[] => {
        const timeSegments = this.currentSegments.filter((s) => s.segmentType === 'TIME');

        return compact(
            timeSegments.map((segment) => {
                const { value } = getKeyValueFromFormFieldSegment(segment, {
                    formFieldSeparator: this.config.tokens.formFieldSeparator,
                    formFieldDelimiter: this.config.tokens.formFieldDelimiter,
                });

                if (value) {
                    return Number(value);
                }

                return null;
            })
        );
    };

    executionValuesForCommandIdAndSegmentType = (
        segmentType: CliArgumentEnumType,
        entityMap?: CommandEntityMap
    ): CliAutocompleteOption['value'][] => {
        const applicableSegments = this.currentSegments?.filter(
            (s) => s.segmentType === segmentType
        );

        const entities = compact(
            applicableSegments.map((segment) => {
                let entityCandidate:
                    | CliAutocompleteOption
                    | LocationStoreView
                    | undefined = entityMap
                    ?.get(segmentType)
                    ?.get((segment.segmentData as string).toUpperCase()) as CliAutocompleteOption;

                // This is a last-ditch effort to get around irregularly shaped strings as a result
                // of user autocomplete selections. If we don't have an exact match, grab the first
                // relevant match based on name.
                if (!entityCandidate) {
                    try {
                        const keys: string[] = entityMap?.get(segmentType)
                            ? Array.from(entityMap?.get(segmentType)?.keys() ?? [])
                            : [];

                        const firstMatchingKey = keys.find((key) =>
                            key.startsWith(segment.segmentData as string)
                        );

                        entityCandidate = entityMap
                            ?.get(segmentType)
                            ?.get(
                                (firstMatchingKey as string).toUpperCase()
                            ) as CliAutocompleteOption;
                    } catch (e) {
                        // Error finding entity - silently handled
                    }
                }
                return entityCandidate?.value;
            })
        );

        return entities;
    };

    executionValuesForMessageRecipient = (
        entityMap?: CommandEntityMap
    ): CliAutocompleteMessageRecipient[] => {
        const messageRecipientSegment = segmentTypes.MESSAGE_RECIPIENT;

        const applicableSegments = this.currentSegments?.filter(
            (s) => s.segmentType === messageRecipientSegment
        );

        const entities = compact(
            applicableSegments.map((segment) => {
                const entityCandidate: CliAutocompleteOption = entityMap
                    ?.get(messageRecipientSegment)
                    ?.get((segment.segmentData as string).toUpperCase()) as CliAutocompleteOption;

                return entityCandidate?.messageRecipient;
            })
        );

        return entities;
    };

    locationExecutionValuesForCommandId = (): LocationStoreView[] => {
        const applicableSegments = this.currentSegments?.filter(
            (s) => s.segmentType === 'LOCATION'
        ) as EventSegment[];

        const locations = compact(
            applicableSegments.map((segment) => {
                return this.savedLocations.get(segment.segmentData.toUpperCase());
            })
        );

        return locations;
    };

    eventExecutionValuesFormCommandIdAndSegmentType = (
        entityMap?: CommandEntityMap
    ): CliAgencyEvent[] => {
        const applicableSegments = this.currentSegments?.filter(
            (s) => s.segmentType === 'EVENT'
        ) as EventSegment[];

        const ret = applicableSegments.map((segment) => {
            return {
                agencyEventNumber: segment.segmentData.replace(this.config.tokens.eventToken!, ''),
                id:
                    (entityMap
                        ?.get('EVENT')
                        ?.get(segment.segmentData as string) as CliAutocompleteOption)?.value ?? 0,
            };
        });

        return ret;
    };

    entityForSegment = (segment: Segment): CliAutocompleteOption | undefined => {
        if (this.currentCommandId && segment.segmentType !== 'COMMAND') {
            const entityMap = this.commandEntityMap.get(this.currentCommandId);

            if (segment.segmentType === 'LOCATION') {
                // TODO: Need to handle locations in entityForSegment
            } else {
                return entityMap?.get(segment.segmentType)?.get(segment.segmentData) as
                    | CliAutocompleteOption
                    | undefined;
            }
        }
    };

    decorate = (entry: NodeEntry): Range[] => {
        const [_node, path] = entry;

        const ranges: Range[] = [];

        this.currentSegments?.forEach((segment) => {
            ranges.push({
                // @ts-expect-error Object literal may only specify known properties, and 'type' does not exist in type 'BaseRange'.
                type: segment.segmentType,
                focus: { path, offset: segment.start + segment.length },
                anchor: { path, offset: segment.start },
            });
        });

        return ranges;
    };

    private openDexFormPanel = ({
        dexForm,
        entityType,
        dexNextEnabled,
    }: {
        dexForm: 'PERSON_MASTER_QUERY' | 'VEHICLE_MASTER_QUERY';
        entityType: QueryableEntityType;
        dexNextEnabled: boolean;
    }): void => {
        let config = {};

        if (dexNextEnabled) {
            const { getFormByEntityType } = this.getDexNextForms();
            const { id: dexNextFormId, displayName: dexNextFormName } =
                getFormByEntityType(entityType) || {};
            config = { dexNextFormId, dexNextFormName };
        }

        this.openWorkspacePanel({
            matchExistingPanel: getDataExchangeFormPanelMatcher({ dexForm, dexNextEnabled }),
            workspacePanelConfig: {
                workspacePanelType: 'DATA_EXCHANGE_FORM',
                dexForm,
                ...config,
            },
        });
    };

    handleCliClientAction = (clientAction: CommandLineClientActionUnionType): void => {
        const {
            disableLegacyMapAndSearches,
            mapWindowEnabled,
            cliSelfInitiateCadNext,
            dexNextEnabled,
        } = this.getEnabledFeaturesConfig();

        if (isOpenAlertBannerCliClientAction(clientAction)) {
            const { warningMessage } = clientAction;

            if (electronBridge && 'isElectronRewrite' in electronBridge) {
                this.toast({
                    status: 'error',
                    description: warningMessage,
                });
            } else {
                this.postLegacyMessage({ type: 'CLI_ALERT_BANNER', payload: warningMessage });
            }
        }

        if (isUpdateEventLocationCliClientAction(clientAction)) {
            if (clientAction.eventPanelView) {
                const { eventId, agencyEventNumber } = clientAction.eventPanelView.metadata;

                this.getOpenEventPanel()({ agencyEventNumber, eventId });

                if (eventId && clientAction.staleLocationMessage) {
                    this.triggerActions([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_PANEL_BANNERS',
                            payload: {
                                agencyEventId: eventId,
                                banners: [
                                    {
                                        status: 'attention',
                                        description: clientAction.staleLocationMessage,
                                    },
                                ],
                            },
                        },
                    ]);
                }
                if (eventId && clientAction.editMode) {
                    this.triggerActions([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_LOCATION',
                            payload: {
                                eventId,
                                locationView: clientAction.eventPanelView.form.location,
                                shouldFocusLocationField:
                                    clientAction.eventPanelFocusField === 'EVENT_LOCATION',
                            },
                        },
                    ]);
                }
            }
        }

        if (isSelfInitiateCliClientAction(clientAction)) {
            if (cliSelfInitiateCadNext) {
                if (clientAction.eventPanelView) {
                    // Accounts for differences in the SI location logic on the BE
                    const { id, agencyEventNumber } = clientAction.cliClientActionAgencyEvent;
                    this.getOpenEventPanel()({ agencyEventNumber, eventId: id });

                    this.triggerActions([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_LOCATION',
                            payload: {
                                eventId: id,
                                locationView: clientAction.eventPanelView.form.location,
                                shouldFocusLocationField:
                                    clientAction.eventPanelFocusField === 'EVENT_LOCATION',
                            },
                        },
                    ]);
                    this.getUserActionTrackerInstance()?.trackNewEvent({
                        newEventTrigger: 'CLI',
                    });
                }
            } else {
                // Maintain legacy SI hack within the new window, will be removed after the new BE is out for everyone.
                const { id, agencyEventNumber } = clientAction.cliClientActionAgencyEvent;
                this.getOpenEventPanel()({ agencyEventNumber, eventId: id });

                if (clientAction.consolidatedLocationView) {
                    this.triggerActions([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_LOCATION',
                            payload: {
                                eventId: id,
                                locationView: clientAction.consolidatedLocationView,
                                shouldFocusLocationField: true,
                            },
                        },
                    ]);
                }
            }
        }

        if (isEditEventCliClientAction(clientAction)) {
            const {
                consolidatedLocationView,
                unitGpsDto,
                shouldFocusLocationField,
                cliClientActionAgencyEvent: { id, agencyEventNumber },
            } = clientAction;

            this.getOpenEventPanel()({
                agencyEventNumber,
                eventId: id,
            });
            if (consolidatedLocationView) {
                this.triggerActions([
                    {
                        id: v4(),
                        targetWindowType: 'EVENT_V2',
                        type: 'SET_EVENT_LOCATION',
                        payload: {
                            eventId: id,
                            locationView: consolidatedLocationView,
                        },
                    },
                ]);
            } else {
                this.triggerActions([
                    {
                        id: v4(),
                        targetWindowType: 'EVENT_V2',
                        type: 'SET_EVENT_LOCATION_FROM_UNIT_LOCATION',
                        payload: {
                            eventId: id,
                            unitLocation: unitGpsDto,
                            shouldFocusLocationField,
                            unitLocationIsStale: !!unitGpsDto?.isStale,
                        },
                    },
                ]);
            }
        }

        if (isOpenEditHospitalFormCliClientAction(clientAction)) {
            this.drawerState.open({
                type: 'edit-hospital',
                props: { hospitalId: clientAction.hospitalId },
            });
        }

        if (isEditUnitCliClientAction(clientAction)) {
            if (clientAction.cliClientActionUnit.isTemporary) {
                this.drawerState?.open({
                    type: 'edit-temporary-unit',
                    props: { unitId: clientAction.cliClientActionUnit.id },
                });
            } else {
                this.drawerState?.open({
                    type: 'log-on-edit-unit',
                    props: { unitId: clientAction.cliClientActionUnit.id },
                });
            }
        }

        if (isOpenRotationForEventCliClientAction(clientAction)) {
            const { agencyEventNumber, id } = clientAction.cliClientActionAgencyEvent;

            this.getOpenEventPanel()({
                agencyEventNumber,
                eventId: id,
            });

            this.triggerActions([
                {
                    id: v4(),
                    targetWindowType: 'EVENT_V2',
                    type: 'OPEN_SERVICE_ROTATION_DRAWER',
                    payload: {
                        agencyEventId: id,
                        serviceRotationTypeId: clientAction.serviceTypeCode,
                        isTowRotation: clientAction.isTowServiceType,
                    },
                },
            ]);
        }

        if (isViewEventCliClientAction(clientAction)) {
            this.getOpenEventPanel()({
                agencyEventNumber: clientAction.cliClientActionAgencyEvent.agencyEventNumber,
                eventId: clientAction.cliClientActionAgencyEvent.id,
            });
        }
        if (isLogoutCliClientAction(clientAction)) {
            if (isElectron()) {
                if (electronBridge && 'isElectronRewrite' in electronBridge) {
                    this.triggerActions([
                        {
                            targetWindowType: 'ROOT',
                            id: v4(),
                            type: 'LOGOUT',
                        },
                    ]);
                } else {
                    this.postLegacyMessage({ type: 'LOGOUT' });
                }
            } else {
                logout(this.apiClient);
            }
        }

        if (isOpenDexResultsCliClientAction(clientAction)) {
            this.openWorkspacePanel({
                workspacePanelConfig: { workspacePanelType: 'DATA_EXCHANGE_RESULTS' },
            });
        }

        if (isOpenDexPersonFormCliClientAction(clientAction)) {
            this.openDexFormPanel({
                dexForm: 'PERSON_MASTER_QUERY',
                entityType: QueryableEntityType.PERSON,
                dexNextEnabled,
            });
        }

        if (isOpenDexVehicleFormCliClientAction(clientAction)) {
            this.openDexFormPanel({
                dexForm: 'VEHICLE_MASTER_QUERY',
                entityType: QueryableEntityType.VEHICLE,
                dexNextEnabled,
            });
        }

        if (isResyncCliClientAction(clientAction)) {
            if (electronBridge && 'isElectronRewrite' in electronBridge) {
                electronBridge.resync();
            } else {
                this.postLegacyMessage({ type: 'RESYNC' });
            }
        }

        if (isOpenBoloFormCliClientAction(clientAction)) {
            this.postLegacyMessage({ type: 'OPEN_BOLO_FORM' });
        }

        if (isGenerateRenCliClientAction(clientAction)) {
            const message = clientAction.cliClientActionAgencyEvents.map((agencyEvent) => {
                return `${
                    this.fieldDisplayNameByFieldName[
                        FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name
                    ]
                } ${agencyEvent.reportingEventNumbers.join(', ')} generated for #${
                    agencyEvent.agencyEventNumber
                }\n`;
            });
            this.toast({ description: message, status: 'default' });
        }
        if (isAgencyAssistanceCliClientAction(clientAction)) {
            const { id } = clientAction.cliClientActionAgencyEvent;
            this.getOpenEventPanel()({
                eventId: id,
            });
            this.triggerActions([
                {
                    targetWindowType: 'EVENT_V2',
                    id: v4(),
                    type: 'SHOW_REQUEST_ASSISTANCE_MODAL',
                    payload: {
                        eventId: id,
                        initialAssistanceAgencyTypes: compact([
                            clientAction.agencyTypeGlobalAttrId,
                        ]) as IntialAssistanceAgencyType[],
                    },
                },
            ]);
        }

        if (isViewUnitHistoryCliClientAction(clientAction)) {
            if (
                (electronBridge && 'isElectronRewrite' in electronBridge) ||
                this.getEnabledFeaturesConfig().unitHistory
            ) {
                this.openUnitHistoryPanel(clientAction.unitOption);
            } else {
                this.postLegacyMessage({
                    type: 'VIEW_UNIT_HISTORY',
                    payload: clientAction.unitOption.value,
                });
            }
        }

        if (isViewEventHistoryCliClientAction(clientAction)) {
            const { agencyEventNumber, id } = clientAction.cliClientActionAgencyEvent;
            this.getOpenEventPanel()({
                eventId: id,
                agencyEventNumber,
                workspacePanelConfig: {
                    panelState: {
                        initialTab: 'EVENT_LOG',
                    },
                },
            });
        }

        if (isShowOnMapCliClientAction(clientAction)) {
            if (clientAction.cliClientActionAgencyEvent) {
                if (disableLegacyMapAndSearches || mapWindowEnabled) {
                    this.getViewLocationOnMap()({
                        entityId: clientAction.cliClientActionAgencyEvent.id,
                        entityType: EsriMapEntityTypeEnum.EVENT,
                    });
                } else {
                    this.postLegacyMessage({
                        type: 'SHOW_EVENT_ON_AVL_MAP',
                        payload: { eventId: clientAction.cliClientActionAgencyEvent.id },
                    });
                }
            }

            if (clientAction.cliClientActionUnit) {
                if (disableLegacyMapAndSearches || mapWindowEnabled) {
                    this.getViewLocationOnMap()({
                        entityId: clientAction.cliClientActionUnit.id,
                        entityType: EsriMapEntityTypeEnum.UNIT,
                    });
                } else {
                    this.postLegacyMessage({
                        type: 'SHOW_UNIT_ON_AVL_MAP',
                        payload: { unitId: clientAction.cliClientActionUnit.id },
                    });
                }
            }
        }
    };
}
